import { Component, Input, inject, HostListener, effect, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';
import { AuthStateService } from '@dental-ui/auth';
import { AppNavigationService } from '@dental-ui/shared/app-navigation';
import { ToastContainerComponent, ConfirmDialogContainerComponent, ThemeService, THEME_PALETTE_OPTIONS, NotificationsComponent } from '@dental-ui/ui';
import { SettingsComponent } from '@dental-ui/settings';
import { calculateInitials, getInitial } from '@dental-ui/util';

export interface ClinicMenuItem {
  label: string;
  route: string;
  icon: string;
}


@Component({
  selector: 'lib-clinic-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe, ToastContainerComponent, ConfirmDialogContainerComponent, SettingsComponent, NotificationsComponent],
  templateUrl: './clinic-layout.component.html',
  styleUrl: './clinic-layout.component.scss',
})
export class ClinicLayoutComponent {
  @Input() menuItems: ClinicMenuItem[] = [];
  @Input() appTitle = 'Clinic Manager';

  private readonly authStateService = inject(AuthStateService);
  private readonly appNavigation = inject(AppNavigationService);
  readonly themeService = inject(ThemeService);
  private readonly translateService = inject(TranslateService);
  readonly themeOptions = THEME_PALETTE_OPTIONS;
  isPaletteOpen = false;

  isProfileOpen = false;
  userFullName = '';
  userEmail = '';
  userInitials = '';
  showSettings = signal(false);
  userAvatarUrl = signal<string | null>(null);

  readonly isAuthorizedForAccount = computed(() => {
    const role = this.authStateService.user()?.role;
    return role === 'AccountManager' || role === 'LaboratoryManager';
  });

  readonly isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  constructor() {
    effect(() => {
      const user = this.authStateService.user();

      const lang = this.translateService.getCurrentLanguage() || 'tr';
      if (user?.firstName && user?.lastName) {
        this.userFullName = `${user.firstName} ${user.lastName}`;
        this.userInitials = calculateInitials(user.firstName, user.lastName, lang);
      } else if (user?.email) {
        this.userFullName = user.email;
        this.userInitials = getInitial(user.email).toUpperCase();
      } else {
        this.userFullName = '';
      }
      this.userEmail = user?.email || '';

      if (user?.userAvatarUrl) {
        const url = user.userAvatarUrl;
        const separator = url.includes('?') ? '&' : '?';
        this.userAvatarUrl.set(`${url}${separator}t=${new Date().getTime()}`);
      } else {
        this.userAvatarUrl.set(null);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.clinic-profile-menu')) {
      this.isProfileOpen = false;
    }
    if (!target.closest('.clinic-theme-palette')) {
      this.isPaletteOpen = false;
    }
  }

  onTabClick(event: MouseEvent): void {
    // Prevent the global document click listener from racing navigation clicks.
    event.stopPropagation();
    this.isProfileOpen = false;
    this.isPaletteOpen = false;
  }

  togglePalette(): void {
    this.isPaletteOpen = !this.isPaletteOpen;
    if (this.isPaletteOpen) {
      this.isProfileOpen = false;
    }
  }

  selectTheme(themeId: string): void {
    this.themeService.setPalette(themeId);
    this.isPaletteOpen = false;
  }

  toggleProfile(): void {
    this.isProfileOpen = !this.isProfileOpen;
    if (this.isProfileOpen) {
      this.isPaletteOpen = false;
    }
  }



  navigateToProfile(): void {
    if (!this.isAuthorizedForAccount()) {
      return;
    }
    this.isProfileOpen = false;
    this.authStateService.navigateToApp('account', '/account');
  }

  logout(): void {
    this.isProfileOpen = false;
    this.authStateService.logout();
  }

  openSettings(): void {
    this.isProfileOpen = false;
    this.translateService.loadLibrary('account').finally(() => {
      this.showSettings.set(true);
    });
  }

  closeSettings(): void {
    this.showSettings.set(false);
  }
}
// rebuild
